import rfdc from "rfdc";
import moment from "moment";
import deepEqual from "deep-equal";
import Format from "./singleton/format";
import Entity from "./singleton/entity";
import IPeriodValue from "./interface/period-value";
import IGroup from "./interface/grouped";
import IField from "./interface/field";
import IEntityIcon from "./interface/entity-icon";
import IEntity from "./interface/entity";
import IDocumentValue from "./interface/document-value";
import IDocument from "./interface/document";
import Global from "./global";
import SortingType from "./enum/sorting-type";
import ErrorCode from "./enum/error-code";
import DatePeriod from "./enum/date-period";
import TimeFormats from "./data/formats-time";
import DateFormats from "./data/formats-date";
import EntityIcons from "./data/entity-icons";

/* Public Methods */

export function getDateFormats(): string[] {
    return DateFormats;
}

export function getTimeFormats(): string[] {
    return TimeFormats;
}

export async function compressImage(originalBase: string, scale: number = Global.IMAGE_COMPRESS_SCALE): Promise<string> {
    if (isBase64String(originalBase)) {
        return new Promise(function (resolve, reject) {
            const img = new Image();
            img.onload = function () {
                const canvas = document.createElement('canvas');
                const proportion = scale / img.height;
                canvas.width = Math.round(img.width * proportion);
                canvas.height = Math.round(img.height * proportion);
                const context = canvas.getContext('2d');
                if (context) {
                    context.scale(proportion, proportion);
                    context.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL());
                } else {
                    reject();
                }
            }
            img.src = originalBase;
        });
    } else {
        throw ErrorCode.BadArguments;
    }
}

export function isValidEmail(email: string): boolean {
    const regex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(email);
}

export function getIcons(pattern: string = ''): string[] {
    return filter(EntityIcons, pattern, ['tags']).map((icon: IEntityIcon) => icon.id);
}

export function regexpFullTest(str: string, regex: RegExp): boolean {
    const result: RegExpMatchArray | null = str.match(regex);
    if (result && result.length === 1) {
        return result[0] === str;
    }
    return false;
}

/* Private Methods */

export function regexpEscape(str: string): string {
    return str.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1');
}

export function occurs(str: string, pattern: string): string[] {
    return str.match(new RegExp(regexpEscape(pattern), 'g')) || []
}

export function replaceAll(str: string, pattern: string, replacement: string = ''): string {
    str = str.replace(new RegExp(regexpEscape(pattern), 'g'), replacement);
    return str;
}

export function normalize(str: string): string {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function digitsOnly(str: string): string {
    return str.replace(/\D/g, '');
}

export function clearLinebreaks(str: string): string {
    return str.replace(/(\r\n|\n|\r)/gm, '');
}

export function log(message: any, color: string = 'green'): void {
    if (typeof message === 'string') {
        console.log(`%c ${message}`, `color: ${color}; font-weight: bold;`);
    } else {
        console.log(message);
    }
}

export function arrayRemove<T>(array: T[], item: T): void {
    const index = array.indexOf(item);
    if (index !== -1) {
        array.splice(index, 1);
    }
}

export function arrayCompare<T>(a: T[], b: T[]): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

export function uniq<T>(array: T[]): T[] {
    return array.filter((value, index) => array.indexOf(value) === index);
}

export function getEntries(source: string, expression: RegExp): string[] {
    return (source.match(expression) || []).map(result => result.toString()) || [];
}

export function getEntryParams(entry: string): (string | number)[] {
    const result: (string | number)[] = [];
    if (entry.indexOf('(') > -1) {
        const array: string[] = entry.slice(entry.indexOf('(') + 1, entry.length - 1).split(';;');
        array.forEach((item: string) => {
            if (item) {
                const num: number = Number(item);
                if (isNaN(num)) {
                    result.push(item);
                } else {
                    result.push(num);
                }
            } else {
                result.push('');
            }
        });
    }
    return result;
}

export function getEntryParamsString(entry: string): string[] {
    return getEntryParams(entry).map((i: (string | number)) => i.toString());
}

export function filter<T>(array: T[], pattern: string, props: (keyof T)[] = []): T[] {
    const p: string = pattern = `${normalize(pattern).toLowerCase()}`;
    const patterns: string[] = uniq(p.split(' ')).filter((value: string) => !!value);
    if (patterns.length) {
        return array.filter((item: T) => {
            let result: boolean = false;
            const matchers: (T | T[keyof T])[] = [];
            if (props.length) {
                for (let i = 0; i < props.length; i++) {
                    matchers.push(item[props[i]]);
                }
            } else {
                matchers.push(item);
            }
            for (let i = 0; i < matchers.length; i++) {
                let matcher: string = '';
                if (typeof matchers[i] === 'number') {
                    matcher = `${matchers[i]}`;
                } else if (typeof matchers[i] === 'string' && matchers[i]) {
                    matcher = normalize(`${matchers[i]}`).toLowerCase();
                }
                if (matcher) {
                    result = true;
                    for (let j = 0; j < patterns.length; j++) {
                        result = result && matcher.includes(patterns[j]);
                    }
                }
            }
            return result;
        });
    } else {
        return array;
    }
}

export function sortBy<T>(array: T[], prop: keyof T, type: SortingType = SortingType.Ascending): T[] {
    array.sort((a: T, b: T): number => {
        let aProp: T[keyof T] = a[prop];
        let bProp: T[keyof T] = b[prop];
        if (typeof aProp === 'string') {
            aProp = aProp.toLowerCase() as unknown as T[keyof T];
        }
        if (typeof bProp === 'string') {
            bProp = bProp.toLowerCase() as unknown as T[keyof T];
        }
        return (aProp > bProp ? 1 : -1) * (type === SortingType.Ascending ? 1 : -1);
    });
    return array;
}

export function sortByOrder<T, P>(array: T[], prop: keyof T, order: number[]): T[] {
    const maxOrder: number = Math.pow(2, 32);
    return array.sort((a: T, b: T) => {
        let aIndex: number = order.indexOf(a[prop] as unknown as number);
        let bIndex: number = order.indexOf(b[prop] as unknown as number);
        aIndex = aIndex < 0 ? maxOrder : aIndex;
        bIndex = bIndex < 0 ? maxOrder : bIndex;
        return aIndex - bIndex;
    });
}

export function isBase64String(base64: string): boolean {
    return base64.indexOf('data:') > -1 && base64.indexOf('base64,') > -1;
}

export function getShortName(original: string): string {
    const separators: string[] = [' ', '.', '_', '-', '@'];
    let splitted: string[] = [];
    const name: string = `${original.toUpperCase().trim()}`;
    for (let i = 0; i < name.length; i++) {
        if (separators.includes(name[i])) {
            splitted = name.split(name[i]);
            break;
        }
    }
    if (splitted.length > 1) {
        return splitted[0][0] + splitted[1][0];
    } else if (name.length > 1) {
        return name.slice(0, 2);
    }
    return name;
}

export function clone<T>(original: T): T {
    return rfdc({})(original);
}

export function equal<T>(a: T, b: T): boolean {
    return deepEqual(a, b);
}

export function deepEncode<T>(obj: T): T {
    if (obj) {
        if (typeof obj === 'string') {
            return escape(obj) as unknown as T;
        } else if (typeof obj === 'object') {
            const r: T = clone(obj);
            Object.keys(r).forEach((k: string) => r[k as keyof T] = deepEncode(r[k as keyof T]));
            return r;
        }
    }
    return obj;
}

export function deepDecode<T>(obj: T): T {
    if (obj) {
        if (typeof obj === 'string') {
            return unescape(obj) as unknown as T;
        } else if (typeof obj === 'object') {
            const r: T = clone(obj);
            Object.keys(r).forEach((k: string) => r[k as keyof T] = deepDecode(r[k as keyof T]));
            return r;
        }
    }
    return obj;
}

export function arrayStartsWith<T>(source: T[], value: T[]): boolean {
    for (let i = 0; i < value.length; i++) {
        if (value[i] !== source[i]) {
            return false;
        }
    }
    return true;
}

interface IMomentMappingItem {

    period: DatePeriod;

    timeKey: moment.RelativeTimeKey;

    timeUnit: moment.unitOfTime.Diff;

}

const MomentMapping: IMomentMappingItem[] = [
    { period: DatePeriod.Second, timeKey: 'ss', timeUnit: 's' },
    { period: DatePeriod.Minute, timeKey: 'mm', timeUnit: 'm' },
    { period: DatePeriod.Hour, timeKey: 'hh', timeUnit: 'h' },
    { period: DatePeriod.Day, timeKey: 'dd', timeUnit: 'd' },
    { period: DatePeriod.Month, timeKey: 'MM', timeUnit: 'M' },
    { period: DatePeriod.Year, timeKey: 'yy', timeUnit: 'y' },
];

export function momentGetUnit(period: DatePeriod): moment.unitOfTime.Diff {
    return (MomentMapping.find((i: IMomentMappingItem) => {
        return i.period === period
    }) || MomentMapping[0]).timeUnit;
}

export function momentGetTimekey(period: DatePeriod): moment.RelativeTimeKey {
    return (MomentMapping.find((i: IMomentMappingItem) => {
        return i.period === period
    }) || MomentMapping[0]).timeKey;
}

export function getDateonlyIso(iso: string): string {
    const date: Date = new Date(iso);
    if (!isNaN(date.getTime())) {
        const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        return utcDate.toISOString().split('T')[0];
    }
    return '';
}

export function flatten<T>(origin: T[][]): T[] {
    return Array.prototype.concat.apply([], origin);
}

export function groupBy<T>(origin: T[], prop: keyof T): IGroup<T> {
    const result: IGroup<T> = {};
    origin.forEach((item: T) => {
        let key: string = '';
        if (item[prop] !== undefined && item[prop] !== null) {
            key = `${item[prop]}`;
        }
        if (result[key]) {
            result[key].push(item);
        } else {
            result[key] = [item];
        }
    });
    return result as IGroup<T>;
}

export function sum(values: number[]): number {
    if (values.length) {
        return values.reduce((a: number, b: number) => a + b);
    }
    return 0;
}

export function getEmptyPeriod(overdue: boolean = false): IPeriodValue {
    return { overdue, Y: 0, M: 0, D: 0, h: 0, m: 0, s: 0, full: null };
}

export async function timeout(time: number = 100): Promise<void> {
    return new Promise((resolve: Function) => setTimeout(resolve, time));
}

export function template(tpl: string, entity: IEntity, fields: number[], related: IDocument): string {
    let value: string = `${tpl}`;
    fields.forEach((id: number) => {
        let val: IDocumentValue = related[`c${id}`];
        const foreignField: IField | null = Entity.getField(entity, id);
        val = Format.byField(val, foreignField);
        value = replaceAll(value, `<%= c${id} %>`, val as string);
    });
    return value;
}

export function longest(arr: string[]): string {
    return arr[arr.reduce((prev: number, cur: string, i: number) => {
        return arr[prev].length > cur.length ? prev : i;
    }, 0)];
}

export function enumEach<T>(e: T, callback: Function): void {
    const keys: (keyof typeof e)[] = <(keyof typeof e)[]>Object.keys(e);
    for (const key of keys) {
        callback(e[key]);
    }
}

export function enumValues<T>(e: T): T[keyof T][] {
    return <(T[keyof T])[]>Object.values(e);
}

export function enumIncludes<T>(e: T, value: string): boolean {
    const values: string[] = enumValues(e).map((v: T[keyof T]) => `${v}`);
    return values.includes(value);
}

export function getPathWeight(pathId: number[]): number {
    const gainer: number = 1000;
    return pathId.reduce((acc: number, value: number, index: number) => {
        return acc + Math.pow(gainer, (pathId.length - index - 1)) * value;
    }, 0)
}

export function parsePathId(str: string): number[] {
    return str.split(',').map((s: string) => Number(s));
}

export function getTimestamp(): number {
    return Date.now();
}

export async function digest(message: string): Promise<string> {
    const hash: ArrayBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(message));
    return Array.prototype.map.call(new Uint8Array(hash), x => ('' + x.toString(16)).slice(-2)).join('');
}

export function escapePostgre<T>(source: T): T {
    const result: T = clone(source);
    const keys: string[] = Object.keys(source);
    keys.forEach((k: string) => {
        const key: keyof T = k as keyof T;
        if (typeof source[key] === 'string') {
            const value: string = source[key] as unknown as string;
            result[key] = replaceAll(value, '\'', '\'\'') as unknown as T[keyof T];
        } else if (typeof source[key] === 'object' && source[key] !== null) {
            result[key] = escapePostgre(result[key]);
        }
    });
    return result;
}

export function mutualInclusion(strA: string, strB: string): boolean {
    const a: string = normalize(strA.trim().toLowerCase());
    const b: string = normalize(strB.trim().toLowerCase());
    return a.includes(b) || b.includes(a);
}

export function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary: string = atob(base64);
    var bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary: string = '';
    const bytes: Uint8Array = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode( bytes[ i ] );
    }
    return btoa(binary);
}

export function validateBase64(value: string): string {
    const prefixStamp: string = ';base64,';
    if (value.includes(prefixStamp)) {
        return value.slice(value.indexOf(prefixStamp) + prefixStamp.length);
    }
    return value;
}